/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";
import { useBranding } from "../../providers/Branding";

type TabKey = "home" | "rewards" | "account";

export const COLLECTED_BOTTOM_NAV_DESKTOP_HEIGHT = 68;
export const COLLECTED_BOTTOM_NAV_DESKTOP_CLEARANCE =
  COLLECTED_BOTTOM_NAV_DESKTOP_HEIGHT + 60;
/**
 * Space the floating phone navigation occupies inside the viewport, plus a
 * small visual gap for controls rendered immediately above it. The pill is
 * ~3.75rem tall and floats 0.75rem above the bottom edge; reserving only that
 * exact height let the single-card wallet badges sit under its shadow on
 * Chrome Android.
 */
export const COLLECTED_BOTTOM_NAV_MOBILE_CLEARANCE =
  "calc(5.5rem + env(safe-area-inset-bottom))";

type Tab = {
  key: TabKey;
  icon: string;
  labelKey: string;
  path: string;
};

const tabs: Tab[] = [
  {
    key: "home",
    icon: "home",
    labelKey: "customer.collected.tab_home",
    path: "/",
  },
  {
    key: "rewards",
    icon: "wallet",
    labelKey: "customer.collected.tab_rewards",
    path: "/collected",
  },
  {
    key: "account",
    icon: "person",
    labelKey: "customer.collected.tab_account",
    path: "/account",
  },
];

/**
 * The shared bottom navigation for the customer app's primary surfaces
 * (home, rewards wallet, account). The active tab is derived from the current
 * route so the same component highlights correctly wherever it is mounted.
 */
export const CollectedBottomNav = defineComponent({
  name: "CollectedBottomNav",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { t } = useLocalization();
    const { branding } = useBranding();

    const activeKey = computed<TabKey>(() => {
      const path = route.path;
      if (path === "/collected" || path.startsWith("/collected/")) {
        return "rewards";
      }
      if (path === "/account" || path.includes("/account")) {
        return "account";
      }
      return "home";
    });

    return () =>
      branding.value?.id === "javshani" ? null : (
        <nav
          class="fixed left-1/2 z-40 w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 overflow-hidden rounded-full bg-base-content/95 p-1.5 text-base-100 shadow-2xl ring-1 ring-inset ring-base-100/15 backdrop-blur-2xl backdrop-saturate-150"
          style={{
            bottom: "max(0.75rem, env(safe-area-inset-bottom))",
            // Deepen the theme's text colour for the glass tint; browsers without
            // relative colour syntax fall back to the bg-base-content class.
            backgroundColor:
              "oklch(from oklch(var(--bc)) calc(l * 0.5) c h / 0.88)",
          }}
          aria-label={t("customer.collected.title")}
        >
          {/* Specular sheen along the top edge for the liquid-glass look. */}
          <span
            class="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-base-100/15 via-transparent to-transparent"
            aria-hidden="true"
          />
          <div class="relative flex gap-0.5">
            {tabs.map((tab) => {
              const active = tab.key === activeKey.value;
              return (
                <button
                  key={tab.key}
                  type="button"
                  class={[
                    "flex min-w-0 flex-auto items-center justify-center gap-1 rounded-full px-1.5 py-2 transition-colors duration-200",
                    active
                      ? "bg-base-100 text-base-content shadow-sm"
                      : "text-base-100/75 hover:text-base-100",
                  ]}
                  aria-current={active ? "page" : undefined}
                  onClick={() => {
                    if (!active) void router.push(tab.path);
                  }}
                >
                  <span
                    class={[
                      "material-symbols-outlined shrink-0 text-xl leading-none",
                      active ? "font-semibold" : "",
                    ]}
                    aria-hidden="true"
                  >
                    {tab.icon}
                  </span>
                  <span class="min-w-0 truncate text-sm font-semibold leading-none">
                    {t(tab.labelKey)}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      );
  },
});
